import { extractDataHybrid } from '@utils/nlpDataExtractor';
import { getSchemaForAction } from '@utils/schemaCache';
import { mapScreenToBadgeKey } from '@utils/pageBadgeMapper';
import { UPDATE_PAGE_BADGES, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import type { ActionTemplate } from '@utils/semanticActionMapper';
import {
  OPEN_IMAGE_CANVAS,
  GENERATE_IMAGE,
  ENHANCE_IMAGE,
  STYLIZE_IMAGE,
  REMOVE_BACKGROUND,
} from '@utils/canvasActionTemplates';
import {
  OPEN_RICH_TEXT_EDITOR,
} from '@utils/editorActionTemplates';

export type WindowComponentType = 'hierarchy' | 'canvas' | 'editor' | 'dataHandler' | 'document' | null;

export interface ActionHandlersConfig {
  dispatch: any;
  navigation: any;
  currentUserQuery: string;
  setExtractedFormData: (data: Record<string, any>) => void;
  setActionRequiringData: (template: ActionTemplate | null) => void;
  setPendingActions: React.Dispatch<React.SetStateAction<any[]>>;
  actionRequiringData: ActionTemplate | null;
  setLastExecutedAction: (action: { template: ActionTemplate; data?: any } | null) => void;
  setShowPostExecutionFeedback: (show: boolean) => void;
  timeoutIdsRef: React.MutableRefObject<NodeJS.Timeout[]>;
  // Window management
  activeWindowComponent: WindowComponentType;
  setActiveWindowComponent: (component: WindowComponentType) => void;
  // Canvas handlers (optional - only needed for canvas actions)
  handleCanvasAIGenerate?: (prompt: string) => Promise<void>;
  handleCanvasAIEnhance?: (type: 'upscale' | 'stylize' | 'remove-background', options?: any) => Promise<void>;
  selectedCanvasLayerId?: string | null;
  canvasLayers?: any[];
  // Chat messages (optional - only needed for canvas/editor actions)
  setChatMessages?: React.Dispatch<React.SetStateAction<any[]>>;
  // Document handlers (optional - only needed for document actions)
  handleCreateDocument?: (documentType: string, title: string, description: string) => Promise<void>;
}

/**
 * Execute an action with optional data
 */
export const createHandleExecuteAction = (config: ActionHandlersConfig) => {
  const {
    dispatch,
    navigation,
    currentUserQuery,
    setExtractedFormData,
    setActionRequiringData,
    setPendingActions,
    actionRequiringData,
    setLastExecutedAction,
    setShowPostExecutionFeedback,
    timeoutIdsRef,
    activeWindowComponent,
    setActiveWindowComponent,
    handleCanvasAIGenerate,
    handleCanvasAIEnhance,
    selectedCanvasLayerId,
    canvasLayers,
    setChatMessages,
    handleCreateDocument,
  } = config;

  return async (template: ActionTemplate, data?: any, sourcePage?: string) => {
    if (template.requiresData && !data) {
      const schema = getSchemaForAction(template.intent) || template.schema;
      if (schema && currentUserQuery) {
        try {
          const extraction = await extractDataHybrid(
            currentUserQuery,
            schema,
            template.description || template.intent
          );

          if (extraction.confidence > 0.6 && extraction.missingFields.length === 0) {
            data = extraction.extractedData;
          } else {
            setExtractedFormData(extraction.extractedData || {});
            setActionRequiringData(template);
            return;
          }
        } catch (error) {
          console.warn('⚠️ [handleExecuteAction] Data extraction failed, showing form with empty data:', error);
          // If extraction fails, still show the form with empty data
          setExtractedFormData({});
          setActionRequiringData(template);
          return;
        }
      } else {
        setActionRequiringData(template);
        return;
      }
    }

    try {
      const executionData = data;

      // Handle canvas-specific actions
      if (template.intent === OPEN_IMAGE_CANVAS || template.intent === GENERATE_IMAGE) {
        // Open canvas window if not already open
        if (activeWindowComponent !== 'canvas') {
          dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Canvas...' } });
          setActiveWindowComponent('canvas');
          await new Promise(resolve => setTimeout(resolve, 300));
          dispatch({ type: HIDE_LOADING_MODAL });
        }

        // If GENERATE_IMAGE or if OPEN_IMAGE_CANVAS with image generation intent, generate the image
        if (template.intent === GENERATE_IMAGE || (template.intent === OPEN_IMAGE_CANVAS && currentUserQuery)) {
          // Extract image prompt from user query
          let imagePrompt = currentUserQuery || '';

          // Remove command words to get the actual image description
          imagePrompt = imagePrompt
            .replace(/^(can you\s+)?(make|create|generate|draw|design)\s+(an?\s+)?(image|picture|graphic)\s+of\s+/i, '')
            .replace(/^(can you\s+)?(make|create|generate|draw|design)\s+/i, '')
            .replace(/^(an?\s+)?(image|picture|graphic)\s+of\s+/i, '')
            .replace(/^(a\s+|an\s+)/i, '') // Remove leading article if present
            .trim();

          // If we have a valid prompt, generate the image
          if (imagePrompt && imagePrompt.length > 0 && handleCanvasAIGenerate) {
            await handleCanvasAIGenerate(imagePrompt);
          } else if (setChatMessages) {
            // No prompt extracted, just open canvas
            const { uniqueId } = require('lodash');
            const { GiftedChat } = require('react-native-gifted-chat');
            setChatMessages((prevMessages) =>
              GiftedChat.append(prevMessages, [{
                _id: uniqueId(),
                text: "Canvas opened! Describe the image you'd like me to generate.",
                createdAt: new Date(),
                system: true,
                received: true
              }])
            );
          }
        }

        setPendingActions(prev => prev.filter(a => a.intent !== template.intent));
        if (actionRequiringData) {
          setActionRequiringData(null);
        }
        setLastExecutedAction({ template, data });
        const feedbackTimeoutId = setTimeout(() => setShowPostExecutionFeedback(true), 1000);
        timeoutIdsRef.current.push(feedbackTimeoutId);
        return;
      }

      // Handle image enhancement actions
      if (template.intent === ENHANCE_IMAGE || template.intent === STYLIZE_IMAGE || template.intent === REMOVE_BACKGROUND) {
        // Open canvas window if not already open
        if (activeWindowComponent !== 'canvas') {
          dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Canvas...' } });
          setActiveWindowComponent('canvas');
          await new Promise(resolve => setTimeout(resolve, 300));
          dispatch({ type: HIDE_LOADING_MODAL });
        }

        // Check if we have a selected image layer
        const hasSelectedImageLayer = selectedCanvasLayerId && canvasLayers?.find(l => l.id === selectedCanvasLayerId)?.type === 'image';

        if (hasSelectedImageLayer && handleCanvasAIEnhance) {
          let enhancementType: 'upscale' | 'stylize' | 'remove-background' = 'upscale';
          if (template.intent === STYLIZE_IMAGE) {
            enhancementType = 'stylize';
          } else if (template.intent === REMOVE_BACKGROUND) {
            enhancementType = 'remove-background';
          }
          await handleCanvasAIEnhance(enhancementType);
        } else if (setChatMessages) {
          const { uniqueId } = require('lodash');
          const { GiftedChat } = require('react-native-gifted-chat');
          setChatMessages((prevMessages) =>
            GiftedChat.append(prevMessages, [{
              _id: uniqueId(),
              text: "Please select an image layer in the canvas to enhance it.",
              createdAt: new Date(),
              system: true,
              received: true
            }])
          );
        }

        setPendingActions(prev => prev.filter(a => a.intent !== template.intent));
        if (actionRequiringData) {
          setActionRequiringData(null);
        }
        setLastExecutedAction({ template, data });
        const feedbackTimeoutId = setTimeout(() => setShowPostExecutionFeedback(true), 1000);
        timeoutIdsRef.current.push(feedbackTimeoutId);
        return;
      }

      // Handle editor actions
      if (template.intent === OPEN_RICH_TEXT_EDITOR) {
        // Open editor window if not already open
        if (activeWindowComponent !== 'editor') {
          dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Editor...' } });
          setActiveWindowComponent('editor');
          await new Promise(resolve => setTimeout(resolve, 300));
          dispatch({ type: HIDE_LOADING_MODAL });
        }

        setPendingActions(prev => prev.filter(a => a.intent !== template.intent));
        if (actionRequiringData) {
          setActionRequiringData(null);
        }
        setLastExecutedAction({ template, data });
        const feedbackTimeoutId = setTimeout(() => setShowPostExecutionFeedback(true), 1000);
        timeoutIdsRef.current.push(feedbackTimeoutId);
        return;
      }

      // Handle data management actions
      if (template.intent === 'MANAGE_DATA_SEQUENCE') {
        // Open dataHandler window if not already open
        if (activeWindowComponent !== 'dataHandler') {
          dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Data Handler...' } });
          setActiveWindowComponent('dataHandler');
          await new Promise(resolve => setTimeout(resolve, 300));
          dispatch({ type: HIDE_LOADING_MODAL });
        }

        setPendingActions(prev => prev.filter(a => a.intent !== template.intent));
        if (actionRequiringData) {
          setActionRequiringData(null);
        }
        setLastExecutedAction({ template, data });
        const feedbackTimeoutId = setTimeout(() => setShowPostExecutionFeedback(true), 1000);
        timeoutIdsRef.current.push(feedbackTimeoutId);
        return;
      }

      // Handle document creation actions
      if (template.intent === 'CREATE_DOCUMENT' && handleCreateDocument && data) {
        const documentType = data.documentType || 'other';
        const title = data.title || 'Untitled Document';
        const description = data.description || '';

        // Open document window if not already open
        if (activeWindowComponent !== 'document') {
          dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Document Editor...' } });
          setActiveWindowComponent('document');
          await new Promise(resolve => setTimeout(resolve, 300));
          dispatch({ type: HIDE_LOADING_MODAL });
        }

        // Create document
        await handleCreateDocument(documentType, title, description);

        setPendingActions(prev => prev.filter(a => a.intent !== template.intent));
        if (actionRequiringData) {
          setActionRequiringData(null);
        }
        setLastExecutedAction({ template, data });
        const feedbackTimeoutId = setTimeout(() => setShowPostExecutionFeedback(true), 1000);
        timeoutIdsRef.current.push(feedbackTimeoutId);
        return;
      }

      if (sourcePage) {
        const badgeKey = mapScreenToBadgeKey(sourcePage);
        if (badgeKey) {
          dispatch({
            type: UPDATE_PAGE_BADGES,
            payload: {
              page: badgeKey,
              count: 1,
            },
          });
        }
      }

      if (template.actionSequence && template.actionSequence.length > 0) {
        template.actionSequence.forEach((actionFn, index) => {
          const timeoutId = setTimeout(() => {
            const action = actionFn(executionData);
            if (action.type === 'REACT_NAVIGATE') {
              const { screen, stack, params } = action.payload;
              if (stack) {
                (navigation as any).navigate(stack, { screen, params });
              } else {
                (navigation as any).navigate(screen, params);
              }
            } else {
              dispatch(action);
            }
          }, index * 100);
          timeoutIdsRef.current.push(timeoutId);
        });
      } else {
        const action = template.reduxAction(executionData);
        if (action.type === 'REACT_NAVIGATE') {
          const { screen, stack, params } = action.payload;
          if (stack) {
            (navigation as any).navigate(stack, { screen, params });
          } else {
            (navigation as any).navigate(screen, params);
          }
        } else {
          dispatch(action);
        }
      }

      setPendingActions((prev) => prev.filter((a) => a.intent !== template.intent));
      if (actionRequiringData) {
        setActionRequiringData(null);
      }
      setLastExecutedAction({ template, data });
      const feedbackTimeoutId = setTimeout(() => setShowPostExecutionFeedback(true), 1000);
      timeoutIdsRef.current.push(feedbackTimeoutId);
    } catch (error) {
      console.error('Error executing action:', error);
    }
  };
};

/**
 * Handle action card click - inject form into chat
 */
export const createHandleActionCardClick = (
  config: ActionHandlersConfig & {
    setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
    handleExecuteAction: (template: ActionTemplate, data?: any, sourcePage?: string) => Promise<void>;
  }
) => {
  const {
    currentUserQuery,
    setChatMessages,
    handleExecuteAction,
  } = config;

  return async (template: ActionTemplate, preExtractedData?: any, sourcePage?: string) => {
    const schema = getSchemaForAction(template.intent) || template.schema;

    if (template.requiresData && schema) {
      let extractedData = preExtractedData || {};
      if (Object.keys(extractedData).length === 0 && currentUserQuery) {
        try {
          const extraction = await extractDataHybrid(
            currentUserQuery,
            schema,
            template.description || template.intent
          );
          extractedData = extraction.extractedData || {};
        } catch (error) {
          console.error('Error extracting data:', error);
        }
      }

      const { uniqueId } = require('lodash');
      const formMessageId = uniqueId();
      const { GiftedChat } = require('react-native-gifted-chat');
      
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: formMessageId,
            text: '',
            createdAt: new Date(),
            system: true,
            formSchema: schema,
            formSubmitted: false,
            formValues: extractedData,
            actionTemplate: template,
            actionSourcePage: sourcePage || 'EasyModeScreen',
            received: true,
          },
        ])
      );
    } else {
      handleExecuteAction(template, undefined, sourcePage || 'EasyModeScreen');
    }
  };
};

